import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { authAPI } from "../services/api";
import toast from "react-hot-toast";
import { AlertCircle, Eye, EyeOff, Loader2, Lock, Mail } from "lucide-react";

const INPUT_CLS =
  "w-full pl-10 pr-3 py-2.5 bg-white border border-zinc-300 rounded-xl text-sm text-zinc-900 placeholder:text-zinc-400 transition focus:outline-none focus:border-coffee-600 focus:ring-4 focus:ring-coffee-600/15 disabled:bg-zinc-50";
const BTN_CLS =
  "w-full inline-flex items-center justify-center gap-2 bg-coffee-600 text-white font-semibold py-2.5 px-4 rounded-xl hover:bg-coffee-700 active:scale-[0.99] transition focus:outline-none focus-visible:ring-4 focus-visible:ring-coffee-600/30 disabled:opacity-60 disabled:cursor-not-allowed";

function LoginPage(): React.ReactElement {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);

  // Forgot password state
  const [showForgot, setShowForgot] = useState<boolean>(false);
  const [forgotEmail, setForgotEmail] = useState<string>("");
  const [forgotLoading, setForgotLoading] = useState<boolean>(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError("");
    setLoading(true);

    const result = await login(formData.email, formData.password);

    setLoading(false);

    if (result.success) {
      navigate("/");
    } else {
      setError(result.message || "Đăng nhập thất bại");
    }
  };

  const handleForgotPassword = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!forgotEmail.trim()) {
      toast.error("Vui lòng nhập email");
      return;
    }
    setForgotLoading(true);
    try {
      await authAPI.forgotPassword(forgotEmail.trim());
      toast.success("Nếu email tồn tại, mật khẩu mới đã được gửi qua hộp thư của bạn.");
      setShowForgot(false);
      setForgotEmail("");
    } catch {
      toast.error("Có lỗi xảy ra. Vui lòng thử lại.");
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.05fr_1fr] bg-coffee-50">
      {/* Bên trái: thương hiệu */}
      <aside className="relative hidden lg:flex flex-col justify-between overflow-hidden bg-gradient-to-br from-coffee-800 via-coffee-700 to-coffee-900 p-12 text-white">
        <div className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-white/5" />
        <div className="pointer-events-none absolute -bottom-32 -left-16 h-[28rem] w-[28rem] rounded-full bg-black/10" />
        <img src="/logo.png" alt="23Homestay" className="relative h-14 w-auto self-start brightness-0 invert" />
        <div className="relative max-w-md">
          <h1 className="font-bold text-4xl leading-tight tracking-tight">
            Quản lý homestay
            <br />
            gọn trong một nơi
          </h1>
          <p className="mt-4 text-coffee-100/80 leading-relaxed">
            Đơn đặt phòng, lịch phòng, kho, doanh thu và đánh giá của khách — theo dõi và xử lý nhanh ngay trên một màn hình.
          </p>
        </div>
        <p className="relative text-sm text-coffee-200/70">© {new Date().getFullYear()} 23 Homestay</p>
      </aside>

      {/* Bên phải: biểu mẫu */}
      <main className="flex items-center justify-center px-5 py-10">
        <div className="w-full max-w-sm">
          <div className="lg:hidden text-center mb-8">
            <img src="/logo.png" alt="23Homestay" className="h-14 w-auto mx-auto" />
          </div>

          {!showForgot ? (
            <>
              <h2 className="text-2xl font-bold text-zinc-900 tracking-tight">Đăng nhập</h2>
              <p className="mt-1 text-sm text-zinc-500">Dành cho nhân viên và quản trị viên 23 Homestay</p>

              {error && (
                <div role="alert" className="mt-5 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3.5 py-3 text-sm text-red-700">
                  <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                <div>
                  <label htmlFor="login-email" className="block text-sm font-medium text-zinc-700 mb-1.5">
                    Email
                  </label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400" />
                    <input
                      id="login-email"
                      type="email"
                      required
                      autoComplete="username"
                      value={formData.email}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                      className={INPUT_CLS}
                      placeholder="ten@23homestay.vn"
                      disabled={loading}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="login-password" className="block text-sm font-medium text-zinc-700 mb-1.5">
                    Mật khẩu
                  </label>
                  <div className="relative">
                    <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400" />
                    <input
                      id="login-password"
                      type={showPassword ? "text" : "password"}
                      required
                      autoComplete="current-password"
                      value={formData.password}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setFormData({ ...formData, password: e.target.value })
                      }
                      className={`${INPUT_CLS} pr-11`}
                      placeholder="••••••••"
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100"
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                <button type="submit" disabled={loading} className={BTN_CLS}>
                  {loading && <Loader2 size={16} className="animate-spin" />}
                  {loading ? "Đang đăng nhập..." : "Đăng nhập"}
                </button>
              </form>

              <div className="mt-5 text-center">
                <button
                  type="button"
                  onClick={() => { setShowForgot(true); setError(""); }}
                  className="text-sm text-coffee-700 hover:text-coffee-900 hover:underline"
                >
                  Quên mật khẩu?
                </button>
              </div>
            </>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-zinc-900 tracking-tight">Quên mật khẩu</h2>
              <p className="mt-1 text-sm text-zinc-500">
                Nhập email tài khoản của bạn. Chúng tôi sẽ gửi mật khẩu mới qua email.
              </p>

              <form onSubmit={handleForgotPassword} className="mt-6 space-y-4">
                <div>
                  <label htmlFor="forgot-email" className="block text-sm font-medium text-zinc-700 mb-1.5">
                    Email
                  </label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400" />
                    <input
                      id="forgot-email"
                      type="email"
                      required
                      value={forgotEmail}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForgotEmail(e.target.value)}
                      className={INPUT_CLS}
                      placeholder="Nhập email tài khoản"
                      disabled={forgotLoading}
                    />
                  </div>
                </div>

                <button type="submit" disabled={forgotLoading} className={BTN_CLS}>
                  {forgotLoading && <Loader2 size={16} className="animate-spin" />}
                  {forgotLoading ? "Đang gửi..." : "Gửi mật khẩu mới"}
                </button>
              </form>

              <div className="mt-5 text-center">
                <button
                  type="button"
                  onClick={() => { setShowForgot(false); setForgotEmail(""); }}
                  className="text-sm text-zinc-500 hover:text-zinc-800 hover:underline"
                >
                  ← Quay lại đăng nhập
                </button>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}

export default LoginPage;
